/**
 * Tags + tag groups. Scalar renders `x-tagGroups` as the top-level sections in
 * the left nav. Two clearly-separated sides of the integration:
 *   1. Server-to-server (your backend → us): API key + HMAC signature. Start
 *      here — create a session, then read games/players/transactions. The
 *      wallet callbacks you implement live on this same server side.
 *   2. Player API (your front-end → us): the player session token only.
 */
export const tags = [
  // --- Server-to-server ---
  {
    name: 'Sessions',
    description:
      'Create a player session with your API key + signature, then hand the returned `session_token` to your front-end. This is the entry point.',
  },
  { name: 'Games (S2S)', description: 'Server-side catalog of available games (packs).' },
  { name: 'Items (S2S)', description: 'Server-side list of the most valuable cards available to pull.' },
  { name: 'Players (S2S)', description: 'Per-player inventory, vault history, summary and status, keyed by account_id.' },
  {
    name: 'Deliveries (S2S)',
    description:
      'Server-side view and control of physical-card deliveries: list a player’s deliveries and cancel a ' +
      'paid one before fulfillment starts. Canceling buys every card back at the regular buyback price (a ' +
      '`credit` callback per card) and refunds the shipping + handling charge, sales tax included (`refund` callback). The player ' +
      'flow itself (addresses, quotes, checkout) lives under Player API → Deliveries.',
  },
  {
    name: 'Shipping promotions (S2S)',
    description:
      'Manage your own shipping promotions from your backend: list them, read one, see the one that is live right now, ' +
      'create, update and disable — the same capability as **Shipping Promotions** in the console, with the same rules. ' +
      'How a promotion prices a delivery, what players see and how it is billed is explained under *Shipping promotions* ' +
      'in the overview. Same API key and signature as the rest of the server-to-server API; only your own promotions are ' +
      'ever visible or writable.\n\n' +
      '**The gate.** Until we have enabled promotions for your account, every call answers ' +
      '`403 SHIPPING_PROMOTIONS_DISABLED`.\n\n' +
      '**Bodies.** A create or an update takes JSON and needs `Content-Type: application/json`; without it the body is ' +
      'not read and the call answers `422 SHIPPING_PROMOTION_REQUIRED`. (curl’s `-d` alone sends a form encoding, which is ' +
      'why the cURL samples set the header.)\n\n' +
      '**Retrying.** There is no idempotency key. A create you repeat — after a timeout, say — is refused with ' +
      '`422 VALIDATION_FAILED` (*Overlaps another shipping promotion*) and makes no second promotion. That refusal looks ' +
      'the same as a real conflict with another promotion, so after a timeout read the promotion back before you treat it ' +
      'as a failure: `GET /shipping_promotions?status=scheduled` for one that starts later, or the list. ' +
      '`GET /shipping_promotions/active` shows only a promotion whose window has already started. Disable is idempotent, ' +
      'and an update with the same body leaves the same state.',
  },
  { name: 'Transactions (S2S)', description: 'Reconciliation ledger of debits / credits / refunds.' },
  {
    name: 'Wallet Callbacks',
    description:
      'Webhooks YOU implement; we call them (signed) to read a balance and move money for a round.\n\n' +
      '**Round lifecycle.** A pack purchase opens a round (`debit`, `round_id = rnd_<order>`). The round ' +
      'stays open until every card in it is resolved, then closes with a single final flag — ' +
      '`round_end_state: true` on a `credit`, or `is_round_final: true` on the money-less `shipping_settle`. ' +
      '**Close the round on the FIRST callback for that `round_id` that carries the flag** (we emit exactly ' +
      'one per round); whichever event resolves the last card carries it. Everything is idempotent on ' +
      '`transaction_id` — treat a repeated `transaction_id` as already-processed (success).\n\n' +
      '**Shipping.** Ordering a delivery sends one consolidated `shipping_debit` (shipping + handling + sales tax, ' +
      'when enabled for your operator; none for a delivery that costs the player nothing), with a `rounds[]` map of each card → its round, ' +
      'a `fees` split of the amount and, while a shipping promotion applies, a `promo` block with the regular amount and the discount (cents). When the warehouse ships ' +
      'a card we send `shipping_settle` (amount 0) to close its round. If a card can’t be shipped, the player is ' +
      'bought back via `credit` (the standard buyback price — `fair_market_value × buyback_percent`, same as a ' +
      'normal sell-back) + `refund` (its handling fee + its share of any sales tax) — no `shipping_settle` for ' +
      'that card.\n\n' +
      '**Example — a pack with 2 cards, one shipped + one undeliverable:** `debit` (open) → `credit` ' +
      '`round_end_state:false` (buyback for the undeliverable card) → `refund` (its handling + tax share) → `shipping_settle` ' +
      '`is_round_final:true` (the other card shipped → round closed).',
  },
  {
    name: 'Event Webhooks',
    description:
      'Optional notifications you subscribe to yourself, in the operator console under **Webhooks**. ' +
      'Add an HTTPS URL, tick the events you want, and press **Send test event** to check it answers ' +
      'before the first real one arrives.\n\n' +
      '**Not the same thing as Wallet Callbacks.** Those are the money protocol: mandatory, one fixed ' +
      'base URL, and we read your answer. These are notifications: optional, any URL you like, and the ' +
      'only thing we look at is the status code — return any 2xx and we are done. Nothing in your ' +
      'response body is parsed.\n\n' +
      '**Signing is identical**, so the verification code you already wrote for the wallet callbacks ' +
      'works unchanged: `X-Repackz-Timestamp` + `X-Repackz-Signature` over the raw JSON bytes. Two extra ' +
      'headers, `X-Repackz-Event` and `X-Repackz-Event-Id`, let you route and de-duplicate without ' +
      'parsing the body first.\n\n' +
      '**Delivery.** At-least-once and unordered — de-duplicate on `event_id`, and never assume two ' +
      'events arrive in the order they happened. A failed attempt is retried on a widening ladder ' +
      '(1m, 5m, 30m, 2h, 12h); after that it stops and waits for you to press Resend in the console, ' +
      'where every attempt and its outcome is listed. An endpoint that keeps failing is paused, and the ' +
      'events that fire meanwhile are recorded as skipped rather than lost.\n\n' +
      'Every event shares one envelope — `event_id`, `event_type`, `occurred_at`, `operator_code`, ' +
      '`data` — so a parser written once keeps working as new event types are added.',
  },
  // --- Player API ---
  { name: 'Catalog', description: 'Browse available games and packs, categories, and the cards you can pull.' },
  { name: 'Purchase & Reveal', description: 'Start an order, charge the wallet, and reveal the cards from a pack.' },
  { name: 'Sell', description: 'Sell cards back (buyback) and credit the wallet.' },
  {
    name: 'Deliveries',
    description:
      'Ship physical cards: addresses, shipping options, a live fee quote, and creating a delivery — which charges the player via your `shipping_debit` callback (none for a delivery that costs the player nothing). When the warehouse fulfills a shipped item we close its round via your `shipping_settle` callback.\n\n' +
      '**You usually don’t call these directly:** the player iframe runs this entire flow for you (address list/add, options, fee quote, payment). Embed the iframe and you implement none of it — only the wallet callbacks. These endpoints are documented for partners who want to build their own shipping UI instead.',
  },
  { name: 'Inventory & Feeds', description: 'A player’s inventory of revealed cards and the public just-pulled feed.' },
  { name: 'Player Misc', description: 'Live wallet balance and the resolved theme.' },
  {
    name: 'Provably Fair',
    description:
      'Tickets, signing keys and receipts that make every rarity roll checkable — see *Provably fair* in the ' +
      'overview for how it works and the exact algorithm. **The player iframe does all of this itself**; these ' +
      'endpoints are documented for partners with their own UI and for anyone verifying a receipt. **Get a ticket** ' +
      'needs the player session token; the keys and receipts are public.\n\n' +
      '| code | HTTP | when | what to do |\n' +
      '|------|------|------|------------|\n' +
      '| `FAIRNESS_DISABLED` | 422 | Provably fair is off for your platform (ticket, or a purchase that carries one) | Buy without `fairness`, or ask us to switch it on. Nothing is charged. |\n' +
      '| `FAIRNESS_NOT_APPLICABLE` | 422 | The player is on your promotion list | Buy without `fairness`. |\n' +
      '| `FAIRNESS_UNAVAILABLE` | 422 | The pack’s odds cannot be fixed right now | Buy without `fairness`, or retry later. |\n' +
      '| `FAIRNESS_RATE_LIMITED` | 429 | More than 30 tickets a minute for the session | Back off. |\n' +
      '| `FAIRNESS_SEED_INVALID` | 422 | `client_seed` is not 64 hex characters | Fix the seed. Nothing is charged. |\n' +
      '| `FAIRNESS_TICKET_EXPIRED` | 422 | Ticket unknown, of another session, or past `valid_until_ms` | Get a new ticket and resend. Nothing is charged. |\n' +
      '| `FAIRNESS_TICKET_USED` | 422 | Ticket already presented, or retired by a newer one | Get a new ticket and resend. Nothing is charged. |\n' +
      '| `FAIRNESS_PARAMS_MISMATCH` | 422 | Pack price or settings changed since the ticket, quantity above `max_quantity`, or a volatility level the ticket has no odds for | Get a new ticket and resend. Nothing is charged. |\n' +
      '| `FAIRNESS_SEED_REUSED` | 422 | The player seed was already used in this session | Generate a new seed, get a new ticket, resend. Nothing is charged. |\n' +
      '| `FAIRNESS_REQUIRED` | 422 | Your platform requires provably fair and the purchase has no ticket | Send `fairness`. Nothing is charged. |\n' +
      '| `TIER_EXHAUSTED` | 422 | The rolled rarity tier sold out during the purchase | The whole purchase is refunded; the roll is never moved to another tier. Let the player buy again. |\n\n' +
      'A refusal that happens after we saw the player seed carries `fairness.server_seed` next to `error_detail`, so ' +
      'the ticket can still be checked.',
  },
  // --- Embed mode ---
  {
    name: 'Embed Mode',
    description:
      'The zero-backend integration: instead of creating sessions server-side, you embed the player iframe ' +
      'with your **embed key** —\n\n' +
      '`https://<iframe_host>/?embed_key=KEY&game_id=ID`\n\n' +
      '— and we handle the players. Visitors browse the catalog anonymously through the `public/*` endpoints ' +
      'below (no credential — just your `embed_key`); at the first purchase the iframe signs them up with ' +
      '**email + password**. Forgotten passwords are reset with a 6-digit code we email. Accounts are ' +
      '**per platform**: the same email on two platforms is two independent accounts.\n\n' +
      '**You normally implement none of this** — the iframe drives the whole flow. The endpoints are ' +
      'documented for transparency and for partners building their own UI. Embed mode is enabled by us per ' +
      'platform (Stripe funding + registration enabled + an issued embed key) — contact us to switch it on. ' +
      'All embed endpoints are rate-limited per IP.',
  },
] as const;

export const tagGroups = [
  {
    name: 'Server-to-server · your backend → us (API key + signature)',
    tags: [
      'Sessions',
      'Games (S2S)',
      'Items (S2S)',
      'Players (S2S)',
      'Deliveries (S2S)',
      'Shipping promotions (S2S)',
      'Transactions (S2S)',
      'Wallet Callbacks',
      'Event Webhooks',
    ],
  },
  {
    name: 'Player API · your front-end → us (player session token)',
    tags: ['Catalog', 'Purchase & Reveal', 'Sell', 'Deliveries', 'Inventory & Feeds', 'Player Misc', 'Provably Fair', 'Embed Mode'],
  },
] as const;
